import React, { useState } from 'react'
import { IconAlertTriangle, IconFolderUp, IconInfoCircle } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from 'src/components/atoms/alert-dialog'
import { PrimaryButton } from 'src/components/atoms/buttons/PrimaryButton'
import Typography from 'src/components/atoms/Typography'
import Badge from 'src/components/atoms/Badge'
import {
  DATA_ROOM_FOLDER_MAX_DEPTH,
  DATA_ROOM_FOLDER_NAME_MAX_LENGTH,
  MAX_FILE_SIZE_BYTES,
} from 'src/enviroment/constants/constants'
import { formatFileSize } from 'src/enviroment/lib/formatters/format-file-size'
import { fileDisplayPath } from 'src/enviroment/utils/fileDisplayPath'
import { formatCount } from 'src/enviroment/utils/formatCount'
import { fileExtension } from 'src/enviroment/utils/fileExtension'
import { cn } from 'src/enviroment/lib/utils'
import { FolderUploadPlan } from 'src/types/projects'

const VISIBLE_ROWS = 5

interface ListRow {
  id: string
  title: string
  subtitle?: string
  meta?: string
}

/** Name first, its folder dimmed below — in a half-width card a full path truncates away the name. */
const toFileRow = (file: File, meta?: string): ListRow => {
  const path = fileDisplayPath(file)
  return { id: path, title: file.name, subtitle: path.slice(0, -file.name.length - 1) || undefined, meta }
}

const extensionOf = (file: File): string => {
  const extension = fileExtension(file.name)
  return extension ? `.${extension}` : 'no extension'
}

/** ".txt 30 · .zip 12" — what a long unsupported list is made of, most common first. */
const countByExtension = (files: File[]): { extension: string; count: number }[] => {
  const counts = new Map<string, number>()
  files.forEach((file) => {
    const extension = extensionOf(file)
    counts.set(extension, (counts.get(extension) ?? 0) + 1)
  })
  return [...counts].map(([extension, count]) => ({ extension, count })).sort((a, b) => b.count - a.count)
}

/** Styled like the detail cards in the project header (`ProjectDetailItem`), with a larger value. */
const StatTile = ({ value, label }: { value: React.ReactNode; label: string }) => (
  <div className="min-w-0 rounded-lg border border-gray-300 px-4 py-2.5">
    <Typography variant="xs" element="p" className="truncate uppercase tracking-wide text-gray-300">
      {label}
    </Typography>
    <Typography variant="xl-bold" element="p" className="mt-1 text-gray-50">
      {value}
    </Typography>
  </div>
)

const ListCard = ({
  title,
  caption,
  rows,
  tone = 'warning',
  children,
}: {
  title: string
  /** One line under the title; side-by-side cards both carry one so their lists start level. */
  caption?: string
  rows: ListRow[]
  tone?: 'warning' | 'danger'
  children?: React.ReactNode
}) => {
  const [showAll, setShowAll] = useState(false)
  const visibleRows = showAll ? rows : rows.slice(0, VISIBLE_ROWS)

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-2',
        tone === 'danger' && 'rounded-lg border border-red-ramo/60 bg-red-ramo/10 p-3',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {tone === 'warning' && <IconAlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />}
          <Typography variant="sm-semibold" className={tone === 'danger' ? 'text-red-400' : 'text-gray-50'}>
            {title}
          </Typography>
        </div>
        <Badge variant={tone === 'danger' ? 'danger' : 'neutral'} className="shrink-0 rounded-full">
          {rows.length}
        </Badge>
      </div>
      {caption && (
        <Typography variant="xs" element="p" className="-mt-1 truncate text-gray-400" title={caption}>
          {caption}
        </Typography>
      )}
      {children}
      <ul className={cn(showAll && 'max-h-60 overflow-y-auto')}>
        {visibleRows.map((row) => (
          <li key={row.id} className="flex items-start gap-2 border-b border-gray-700/60 py-2 last:border-b-0">
            <div className="min-w-0 flex-1">
              <Typography variant="sm" element="p" className="truncate text-gray-100" title={row.title}>
                {row.title}
              </Typography>
              {row.subtitle && (
                <Typography variant="xs" element="p" className="truncate text-gray-400" title={row.subtitle}>
                  {row.subtitle}
                </Typography>
              )}
            </div>
            {row.meta && (
              <Typography variant="sm" className="shrink-0 text-gray-300">
                {row.meta}
              </Typography>
            )}
          </li>
        ))}
      </ul>
      {rows.length > VISIBLE_ROWS && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="self-start text-sm text-light-green-500 underline-offset-2 hover:underline"
        >
          {showAll ? 'Show less' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  )
}

interface UploadFolderModalProps {
  plan: FolderUploadPlan | null
  rootExists: boolean
  onConfirm: () => void
  onCancel: () => void
}

const UploadFolderModal: React.FC<UploadFolderModalProps> = ({ plan, rootExists, onConfirm, onCancel }) => {
  if (!plan) return null

  const skippedGroups = [
    {
      title: 'Unsupported file type',
      rows: plan.skipped.unsupported.map((file) => toFileRow(file)),
      caption: countByExtension(plan.skipped.unsupported)
        .map(({ extension, count }) => `${extension} ${count}`)
        .join(' · '),
    },
    {
      title: 'File too large',
      caption: `Max ${formatFileSize(MAX_FILE_SIZE_BYTES)} per file`,
      rows: [...plan.skipped.oversized]
        .sort((a, b) => b.size - a.size)
        .map((file) => toFileRow(file, formatFileSize(file.size))),
    },
  ].filter((group) => group.rows.length > 0)
  const skippedCount = plan.skipped.unsupported.length + plan.skipped.oversized.length

  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent className="max-w-2xl">
        <div className="flex min-w-0 flex-col gap-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-700">
              <IconFolderUp className="h-6 w-6 text-light-green-500" />
            </div>
            <AlertDialogTitle className="truncate font-inter text-xl font-bold text-gray-50">
              Upload &quot;{plan.rootName}&quot;
            </AlertDialogTitle>
          </div>

          <AlertDialogDescription className="sr-only">
            {`${formatCount(plan.uploads.length, 'file')} in ${formatCount(
              plan.folderPaths.flat().length,
              'folder',
            )}, ${formatFileSize(plan.totalBytes)}`}
          </AlertDialogDescription>
          <div className="grid grid-cols-3 gap-3">
            <StatTile
              value={
                skippedCount > 0 ? (
                  <>
                    {plan.uploads.length}
                    <span className="text-gray-400"> / {plan.uploads.length + skippedCount}</span>
                  </>
                ) : (
                  plan.uploads.length
                )
              }
              label="Files to upload"
            />
            <StatTile value={plan.folderPaths.flat().length} label="Folders" />
            <StatTile value={formatFileSize(plan.totalBytes)} label="Total size" />
          </div>

          {rootExists && (
            <div className="flex items-start gap-2 rounded-lg border border-light-green-500/50 px-4 py-2.5">
              <IconInfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-light-green-500" />
              <Typography variant="sm" className="text-gray-50">
                &quot;{plan.rootName}&quot; is already here — only files that aren&apos;t in it yet will be uploaded.
              </Typography>
            </div>
          )}

          {plan.invalidFolders.length > 0 && (
            <ListCard
              tone="danger"
              title={`${formatCount(plan.invalidFolders.length, 'folder')} can't be uploaded`}
              rows={plan.invalidFolders.map(({ path, reason }) => ({
                id: `${reason}:${path}`,
                title: `"${path}"`,
                subtitle:
                  reason === 'name' ? 'Name not allowed' : `More than ${DATA_ROOM_FOLDER_MAX_DEPTH} levels deep`,
              }))}
            >
              <div className="flex items-start gap-2">
                <IconAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                <Typography variant="xs" className="text-gray-200">
                  Rename or move them and try again. Names are 1-{DATA_ROOM_FOLDER_NAME_MAX_LENGTH} characters, with no
                  slashes or surrounding spaces.
                </Typography>
              </div>
            </ListCard>
          )}

          {skippedGroups.length > 0 && (
            <div className={cn('grid gap-6', skippedGroups.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
              {skippedGroups.map((group) => (
                <ListCard key={group.title} title={group.title} caption={group.caption} rows={group.rows} />
              ))}
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} className="w-40">
            Cancel
          </AlertDialogCancel>
          <PrimaryButton disabled={plan.invalidFolders.length > 0} onClick={onConfirm} className="w-40">
            Upload
          </PrimaryButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default UploadFolderModal
